import React, { useState, useEffect, useRef } from "react";
import { Upload, X } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import Modal from "./ui/Modal";
import { InlineLoadSkeleton } from "./common/InlineLoadSkeleton";
import {
  validateClientName,
  CLIENT_NAME_MAX_LENGTH,
  CLIENT_DESCRIPTION_MAX_LENGTH,
} from "../utils/validationUtils";

const ACCEPTED_LOGO_TYPES = "image/jpeg,image/jpg,image/png,image/webp";
const MAX_LOGO_SIZE_MB = 25;

const ClientFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  client = null,
  loading = false,
}) => {
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    client_name: "",
    client_description: "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoToRemove, setLogoToRemove] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (client) {
      setFormData({
        client_name: client.client_name || "",
        client_description: client.client_description || "",
      });
      setLogoPreview(client.logo_url || null);
      setLogoFile(null);
      setLogoToRemove(false);
    } else {
      setFormData({
        client_name: "",
        client_description: "",
      });
      setLogoFile(null);
      setLogoPreview(null);
      setLogoToRemove(false);
    }
    setErrors({});
  }, [client, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    const clientNameError = validateClientName(formData.client_name);
    if (clientNameError) newErrors.client_name = clientNameError;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle character limit for client_name (50) and client_description (300)
    if (name === "client_name" && value.length > CLIENT_NAME_MAX_LENGTH) return;
    if (
      name === "client_description" &&
      value.length > CLIENT_DESCRIPTION_MAX_LENGTH
    )
      return;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_SIZE_MB * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        logo: `Logo must be under ${MAX_LOGO_SIZE_MB}MB`,
      }));
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setLogoToRemove(false);
    setErrors((prev) => ({ ...prev, logo: "" }));
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setLogoToRemove(true);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit({
        ...formData,
        logoFile: logoFile || undefined,
        logoToRemove: logoToRemove || undefined,
      });
      onClose();
    } catch (error) {
      console.error("Error submitting client form:", error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={client ? "Edit Client" : "Create New Client"}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
        {/* Client Name */}
        <div>
          <div className="flex justify-between mb-1">
            <Label htmlFor="client_name">Client Name</Label>
            <span
              className={`text-sm ${formData.client_name.length > CLIENT_NAME_MAX_LENGTH ? "text-red-500" : "text-gray-500"}`}
            >
              {formData.client_name.length}/{CLIENT_NAME_MAX_LENGTH}
            </span>
          </div>
          <Input
            id="client_name"
            name="client_name"
            value={formData.client_name}
            onChange={handleInputChange}
            placeholder="Enter client name (letters, numbers, spaces, . - ' & , ( ))"
            maxLength={CLIENT_NAME_MAX_LENGTH}
            required
            className={errors.client_name ? "border-red-500" : ""}
          />
          {errors.client_name && (
            <p className="text-sm text-red-500 mt-1">{errors.client_name}</p>
          )}
        </div>

        {/* Client Logo */}
        <div>
          <Label>Client Logo (Optional)</Label>
          <div className="mt-2 flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-bg-tertiary flex items-center justify-center overflow-hidden border-2 border-border-primary flex-shrink-0">
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <Upload className="w-6 h-6 text-text-tertiary" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_LOGO_TYPES}
                onChange={handleLogoChange}
                className="hidden"
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-4 h-4 mr-1" />
                  {logoPreview || logoFile ? "Change" : "Upload"} Logo
                </Button>
                {(logoPreview || logoFile) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRemoveLogo}
                    className="text-red-600 hover:text-red-700"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-xs text-text-tertiary">
                PNG, JPG, WebP. Max {MAX_LOGO_SIZE_MB}MB.
              </p>
            </div>
          </div>
          {errors.logo && (
            <p className="text-sm text-red-500 mt-1">{errors.logo}</p>
          )}
        </div>

        {/* Client Description */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label htmlFor="client_description">
              Client Description (Optional)
            </Label>
            <span
              className={`text-sm ${formData.client_description.length > CLIENT_DESCRIPTION_MAX_LENGTH ? "text-red-500" : "text-gray-500"}`}
            >
              {formData.client_description.length}/
              {CLIENT_DESCRIPTION_MAX_LENGTH}
            </span>
          </div>
          <Textarea
            id="client_description"
            name="client_description"
            value={formData.client_description}
            onChange={handleInputChange}
            placeholder="Enter client description (max 300 characters)"
            rows={4}
            maxLength={CLIENT_DESCRIPTION_MAX_LENGTH}
            className={
              formData.client_description.length > CLIENT_DESCRIPTION_MAX_LENGTH
                ? "border-red-500"
                : ""
            }
          />
          {formData.client_description.length >
            CLIENT_DESCRIPTION_MAX_LENGTH && (
            <p className="text-red-500 text-sm mt-1">
              Character limit exceeded. Maximum {CLIENT_DESCRIPTION_MAX_LENGTH}{" "}
              characters allowed.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex space-x-3 pt-4">
          <Button
            type="button"
            onClick={onClose}
            disabled={loading}
            variant="outline"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button type="submit" disabled={loading} className="flex-1">
            {loading ? (
              <InlineLoadSkeleton className="h-4 w-4" />
            ) : (
              <span>{client ? "Update Client" : "Create Client"}</span>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ClientFormModal;
